import { useMemo, useState } from "react";
import { toast } from "sonner";
import { formatINR } from "@/lib/constants";
import { FileDown, CheckSquare, Square, Calendar } from "lucide-react";

const FIELDS = [
  { key: "created_at", label: "Date", get: (l) => new Date(l.created_at).toLocaleDateString("en-IN") },
  { key: "company_name", label: "Company", get: (l) => l.company_name },
  { key: "location", label: "Location", get: (l) => l.location },
  { key: "phone", label: "Phone", get: (l) => l.phone },
  { key: "lead_source", label: "Source", get: (l) => l.lead_source },
  { key: "product", label: "Product", get: (l) => l.product },
  { key: "all_products", label: "All products", get: (l) => (Array.isArray(l.items) && l.items.length ? l.items.map((it) => `${it.product} x ${it.quantity}`).join("; ") : `${l.product} x ${l.quantity}`) },
  { key: "quantity", label: "Quantity", get: (l) => l.quantity },
  { key: "potential", label: "Potential", get: (l) => l.potential },
  { key: "status", label: "Status", get: (l) => l.status },
  { key: "assigned_rep", label: "Assigned rep", get: (l) => l.assigned_rep || "Unassigned" },
  { key: "sales_value", label: "Sales value (INR)", get: (l) => l.sales_value || 0 },
  { key: "notes", label: "Client requirement", get: (l) => l.notes || "" },
];

const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const toISO = (d) => d.toISOString().slice(0, 10);

export default function Reports({ leads }) {
  const defaults = useMemo(() => {
    const today = new Date();
    let earliest = today;
    leads.forEach((l) => {
      const d = new Date(l.created_at);
      if (!isNaN(d) && d < earliest) earliest = d;
    });
    return { from: toISO(earliest), to: toISO(today) };
  }, [leads]);

  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [selected, setSelected] = useState(
    Object.fromEntries(FIELDS.map((f) => [f.key, true]))
  );

  const inRange = useMemo(() => {
    return leads.filter((l) => {
      const d = new Date(l.created_at);
      if (from && d < new Date(from + "T00:00:00")) return false;
      if (to && d > new Date(to + "T23:59:59")) return false;
      return true;
    });
  }, [leads, from, to]);

  const totalRevenue = useMemo(
    () => inRange.reduce((s, l) => s + (Number(l.sales_value) || 0), 0),
    [inRange]
  );

  const selectedCount = FIELDS.filter((f) => selected[f.key]).length;

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));
  const setAll = (val) =>
    setSelected(Object.fromEntries(FIELDS.map((f) => [f.key, val])));

  const download = () => {
    if (inRange.length === 0) {
      toast.error("No leads found in this date range");
      return;
    }
    const cols = FIELDS.filter((f) => selected[f.key]);
    if (cols.length === 0) {
      toast.error("Select at least one column to export");
      return;
    }
    const header = cols.map((c) => esc(c.label)).join(",");
    const rows = inRange.map((l) => cols.map((c) => esc(c.get(l))).join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `techborn-leads_${from}_to_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${inRange.length} leads`);
  };

  const inputCls =
    "h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500";

  return (
    <div className="p-6 sm:p-8 max-w-4xl w-full mx-auto" data-testid="reports-page">
      <header className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.14em] text-blue-600 font-semibold">
          Reports & export
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">
          Download your leads report
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Pick a date range and choose exactly which columns to include, then export to CSV.
        </p>
      </header>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={16} className="text-slate-500" />
          <h2 className="font-heading text-base font-semibold text-slate-800">Date range</h2>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-1.5 block">
              From
            </label>
            <input
              data-testid="report-from-date"
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-1.5 block">
              To
            </label>
            <input
              data-testid="report-to-date"
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className={inputCls}
            />
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs text-slate-500">Leads in range</div>
            <div className="font-heading text-2xl font-bold text-slate-900" data-testid="report-count">
              {inRange.length}
            </div>
            <div className="text-xs text-emerald-700 font-semibold">
              {formatINR(totalRevenue)} total value
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-base font-semibold text-slate-800">
            Columns to include ({selectedCount})
          </h2>
          <div className="flex gap-2">
            <button
              data-testid="select-all-cols"
              onClick={() => setAll(true)}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700"
            >
              Select all
            </button>
            <span className="text-slate-300">·</span>
            <button
              data-testid="clear-all-cols"
              onClick={() => setAll(false)}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700"
            >
              Clear
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {FIELDS.map((f) => (
            <button
              key={f.key}
              data-testid={`col-${f.key}`}
              onClick={() => toggle(f.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm text-left ${
                selected[f.key]
                  ? "border-blue-200 bg-blue-50 text-blue-800"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
              style={{ transition: "background-color 160ms ease" }}
            >
              {selected[f.key] ? (
                <CheckSquare size={16} className="text-blue-600 shrink-0" />
              ) : (
                <Square size={16} className="text-slate-300 shrink-0" />
              )}
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          Exports as a CSV file you can open in Excel or Google Sheets.
        </div>
        <button
          data-testid="download-report-btn"
          onClick={download}
          className="inline-flex items-center gap-2 h-11 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-md shadow-sm"
          style={{ transition: "background-color 160ms ease" }}
        >
          <FileDown size={18} />
          Download report
        </button>
      </div>
    </div>
  );
}
